/* eslint-disable no-restricted-globals */
import { useEffect, useRef, MutableRefObject } from "react";

import { TStore } from "../components/anchor-provider";
import { getElementScrollPosition } from "../utils/get-element-scroll-position";
import { getHashTargetId } from "../utils/get-hash-target-id";

// How long the targeted section is kept in view after the page is opened,
// so late layout changes (subheader mounting and scrolling to the top,
// header height changes, images loading) don't move it away
export const INITIAL_HASH_SETTLE_TIME = 2000;

const USER_INTERACTION_EVENTS = ["wheel", "touchmove", "keydown", "mousedown"];

// Global CSS sets "scroll-behavior: smooth" on every element, and a smooth
// scroll gets cancelled by any other scroll call, so jump instantly instead
const scrollInstantly = (scroller: TStore["scroller"], top: number) => {
  const element = scroller || document.documentElement;
  const previousBehavior = element.style.scrollBehavior;

  element.style.scrollBehavior = "auto";
  (scroller || window).scrollTo(0, top);
  element.style.scrollBehavior = previousBehavior;
};

// Scrolls to the section targeted by the URL hash when the page is opened
// from a link, and keeps the scroll listener from rewriting the hash until
// the page has settled
export function useInitialHashScroll(
  ref: MutableRefObject<TStore>,
  offset: number,
  setBlockScrollEvent: (value: boolean) => void
) {
  const offsetRef = useRef(offset);
  offsetRef.current = offset;

  useEffect(() => {
    const targetId = getHashTargetId();

    if (!targetId) {
      return;
    }

    const startedAt = Date.now();
    let frame: number | null = null;

    const removeInteractionListeners = () => {
      USER_INTERACTION_EVENTS.forEach(event => removeEventListener(event, release));
    };

    function release() {
      if (frame !== null) {
        cancelAnimationFrame(frame);
        frame = null;
      }
      removeInteractionListeners();
      setBlockScrollEvent(false);
    }

    const keepTargetInView = () => {
      const { sections, scroller } = ref.current;
      const target = sections.find(section => section.id === targetId);

      // Sections are registered on mount, so a missing one isn't rendered
      // on this page (e.g. no investor reports for this opportunity)
      if (!target) {
        release();
        return;
      }

      const top = Math.max(getElementScrollPosition(target, scroller) - offsetRef.current + 1, 0);
      const current = scroller ? scroller.scrollTop : window.pageYOffset;

      if (Math.abs(current - top) > 1) {
        scrollInstantly(scroller, top);
      }

      if (Date.now() - startedAt >= INITIAL_HASH_SETTLE_TIME) {
        release();
        return;
      }

      frame = requestAnimationFrame(keepTargetInView);
    };

    USER_INTERACTION_EVENTS.forEach(event => addEventListener(event, release));
    frame = requestAnimationFrame(keepTargetInView);

    return () => {
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
      removeInteractionListeners();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
